<template>
  <div
    v-if="open"
    class="app-notifs"
    role="dialog"
    aria-label="Notifications"
    data-test="app-shell-notifications-panel"
    @keydown="onKeydown"
  >
    <div class="app-notifs__head">
      <span class="app-notifs__title">Notifications</span>
      <span v-if="unreadCount > 0" class="app-notifs__count" data-test="app-notifs-count">{{ unreadCount }} unread</span>
      <button ref="closeBtn" type="button" class="app-notifs__close" aria-label="Close notifications" @click="$emit('close')">
        <AppIcon name="x" :size="16" />
      </button>
    </div>

    <!-- Desktop alerts (moved here from a full-width card above the page, 2026-10-13). First the ask,
         then, after Enable, the result of the test sound. Layout.vue owns the state and the actions. -->
    <div v-if="alertsPrompt.phase === 'ask'" class="app-notifs__alerts app-notifs__alerts--ask" data-test="app-notifs-alerts-ask">
      <div class="app-notifs__alerts-title">Get alerts for new chats &amp; SMS</div>
      <p class="app-notifs__alerts-text">Click Enable to allow desktop notifications. Sound is a separate browser setting, so make sure "Sound" is allowed in your site settings too.</p>
      <div class="app-notifs__alerts-actions">
        <button type="button" class="app-notifs__alerts-primary" data-test="app-notifs-alerts-enable" @click="$emit('alerts-enable')">Enable</button>
        <button type="button" class="app-notifs__alerts-secondary" data-test="app-notifs-alerts-not-now" @click="$emit('alerts-not-now')">Not now</button>
      </div>
    </div>
    <div
      v-else-if="alertsPrompt.phase === 'sound'"
      class="app-notifs__alerts"
      :class="'app-notifs__alerts--' + soundCopy.variant"
      data-test="app-notifs-alerts-sound"
    >
      <div class="app-notifs__alerts-title">{{ soundCopy.title }}</div>
      <p class="app-notifs__alerts-text" v-html="soundCopy.body"></p>
      <div class="app-notifs__alerts-actions">
        <button type="button" class="app-notifs__alerts-primary" data-test="app-notifs-alerts-replay" @click="$emit('alerts-sound-replay')">Test again</button>
        <button type="button" class="app-notifs__alerts-secondary" data-test="app-notifs-alerts-done" @click="$emit('alerts-sound-dismiss')">Done</button>
      </div>
    </div>

    <!-- The classic list, unchanged (its rows, Mark All as Read and store wiring); only restyled
         below with ::v-deep under .app-notifs. A faithful rebuild of the rows is a follow-on. -->
    <div class="app-notifs__body">
      <Notifications />
    </div>

    <button
      type="button"
      class="app-notifs__footer"
      data-test="app-notifs-open-tasks"
      @click="openTasks"
    >Open my tasks</button>
  </div>
</template>

<script>
/**
 * App shell — the bell's popover (design 1d, 380px; full width under the bar on phones, 1h).
 * Wraps the classic Notifications.vue as-is inside the new chrome and adds the "Open my tasks"
 * footer. AppShell owns open/close: `open` in, `close` out (Escape, the ×, or Open my tasks).
 *
 * props  open, unreadCount, alertsPrompt ({ phase: 'ask'|'sound'|'', result } — the desktop-alerts row)
 * emits close, open-tasks, alerts-enable, alerts-not-now, alerts-sound-replay, alerts-sound-dismiss
 */
import AppIcon from './AppIcon.vue';
import Notifications from '@/components/Notifications/Notifications.vue';
import { soundTestCopy } from '@/utils/notificationPromptCopy';

export default {
  name: 'AppNotificationsPopover',
  components: { AppIcon, Notifications },

  props: {
    open: { type: Boolean, default: false },
    unreadCount: { type: Number, default: 0 },
    alertsPrompt: { type: Object, default: () => ({ phase: '', result: '' }) },
  },

  computed: {
    soundCopy() {
      return soundTestCopy(this.alertsPrompt && this.alertsPrompt.result);
    },
  },

  watch: {
    open(isOpen) {
      if (!isOpen) return;
      this.$nextTick(() => {
        const btn = this.$refs.closeBtn;
        if (btn && typeof btn.focus === 'function') btn.focus();
      });
    },
  },

  methods: {
    onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        e.stopPropagation();
        this.$emit('close');
      }
    },

    openTasks() {
      this.$emit('open-tasks');
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './shell';

.app-notifs {
  position: absolute;
  top: 56px;
  right: 150px;
  z-index: $shell-z-popover;
  width: 380px;
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 72px);
  display: flex;
  flex-direction: column;
  background: $fu-surface;
  border: 1px solid $fu-border;
  border-radius: 14px;
  box-shadow: $fu-shadow-lg;
  overflow: hidden;
  font-family: $fu-font-sans;
  color: $fu-text;
  animation: app-notifs-pop 0.14s ease;
}

@keyframes app-notifs-pop {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
}

.app-notifs__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 10px 12px 16px;
  flex: none;
}

.app-notifs__title { font-size: 15px; font-weight: $fu-fw-bold; }
.app-notifs__count { font-size: 12.5px; color: $fu-neutral-500; }

.app-notifs__close {
  @include fu-icon-btn(32px);
  margin-left: auto;
}

// Desktop-alerts row: amber while it asks; the test-sound result is green / red / blue.
.app-notifs__alerts {
  flex: none;
  margin: 0 12px 12px;
  padding: 11px 12px;
  border-radius: 10px;
  background: $fu-info-subtle;
  border: 1px solid #cfe0fb;

  &--ask { background: $fu-warning-subtle; border-color: #f3d38a; }
  &--success { background: $fu-success-subtle; border-color: #bfe5d5; }
  &--error { background: $fu-danger-subtle; border-color: $fu-danger-border; }
  &--ask .app-notifs__alerts-title { color: $fu-warning-text; }
  &--error .app-notifs__alerts-title { color: $fu-danger-text; }
}

.app-notifs__alerts-title { font-size: 13px; font-weight: $fu-fw-bold; }

.app-notifs__alerts-text {
  margin: 4px 0 0;
  font-size: 12.5px;
  line-height: 1.4;
  color: $fu-neutral-600;
}

.app-notifs__alerts-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.app-notifs__alerts-primary,
.app-notifs__alerts-secondary {
  @include fu-button-reset;
  @include fu-focus-ring;
  height: 30px;
  padding: 0 14px;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: $fu-fw-bold;
}

.app-notifs__alerts-primary { background: $fu-brand; color: #fff; &:hover { background: $fu-brand-hover; } }
.app-notifs__alerts-secondary { background: $fu-surface; border: 1px solid $fu-border; color: $fu-neutral-600; &:hover { background: $fu-neutral-50; } }

.app-notifs__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-top: 1px solid $fu-border;

  // The classic card: drop its own chrome (title line, card borders/shadow) — ours replaces it.
  ::v-deep .notifications.card {
    margin: 0;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    background: transparent;
    width: auto;
    max-width: none;
  }

  ::v-deep .notifications .cardHeader { display: none; }

  ::v-deep .notifications .list-group,
  ::v-deep .notifications .list-group-item {
    border-left: 0;
    border-right: 0;
    border-radius: 0;
  }

  ::v-deep .notifications .cardFooter {
    padding: 8px 16px;
    border-top: 1px solid $fu-border;
    background: $fu-surface;
  }

  ::v-deep .notifications .btnNotificationsReload {
    color: $fu-brand-700;
    font-weight: $fu-fw-bold;
  }
}

.app-notifs__footer {
  @include fu-button-reset;
  @include fu-focus-ring;
  flex: none;
  display: block;
  width: 100%;
  padding: 11px 16px;
  border-top: 1px solid $fu-border;
  background: $fu-neutral-25;
  font-size: 13px;
  font-weight: $fu-fw-bold;
  color: $fu-brand-700;
  text-align: left;

  &:hover { background: $fu-neutral-50; }
}

// Tablet: the bell sits further right (no credits name block).
@include shell-tablet {
  .app-notifs { right: 60px; }
}

// Phone (1h): full width under the bar, down to the tab bar.
@include shell-phone {
  .app-notifs {
    position: fixed;
    top: $shell-bar-height;
    right: 0;
    bottom: $shell-tabbar-height;
    left: 0;
    width: auto;
    max-width: none;
    max-height: none;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .app-notifs__close { width: $shell-touch-target; height: $shell-touch-target; }
  .app-notifs__footer { min-height: $shell-touch-target; }
}
</style>
